// Parcours "Acheter un billet de car" — reproduction stricte de la planche AYA Car.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  MapPin, Calendar, ArrowUpDown, Check, Wifi, Snowflake, User, ArrowRight, Share2, Download,
  XCircle, Clock, Armchair, CreditCard, Camera, Contact, LifeBuoy, RefreshCw,
} from "lucide-react";
import "./transport.css";
import { BookingHeader, Pills, PrimaryBtn, Stepper, OptRow, PriceRow, BusSeatMap, SecureNote, RatingLine } from "./components";
import { BUS_CITIES, BUSES, BUS_OPTIONS, BUS_TAKEN, PAY_METHODS, fmt, makeRef } from "./transportData";
import { saveTicket } from "./ticketStore";

const STEPS = ["search", "results", "seat", "options", "passenger", "payment", "confirmation", "ticket", "details"];
const TITLES = {
  search: "Acheter un billet de car", results: "Trajets disponibles", seat: "Choisir votre siège",
  options: "Options de voyage", passenger: "Informations voyageurs", payment: "Paiement",
  ticket: "Mon billet", details: "Détails du voyage",
};
const MOBILE_PAY = [PAY_METHODS[0], PAY_METHODS[1], PAY_METHODS[2], { id: "moov", name: "Moov Money", bg: "#1E40AF", text: "Moov" }];

const BusSummary = ({ bus, from, to, dateLabel, right }) => (
  <div className="tp-summary">
    <img src="/aya/transport/tp_car.jpg" alt="" style={{ width: 46, height: 36, borderRadius: 9, objectFit: "cover", flexShrink: 0 }} />
    <span className="t">
      <h3>{bus.company} <span style={{ color: "#7B8B7F", fontWeight: 700 }}>· {from} → {to}</span></h3>
      <p>{dateLabel} · {bus.dep} → {bus.arr}</p>
    </span>
    {right}
  </div>
);

const BusLegend = () => (
  <div className="tp-legend" data-testid="bus-seat-legend">
    <span><i className="dot" style={{ background: "#10B981" }} />Disponible</span>
    <span><i className="dot" style={{ background: "#9AA79E" }} />Occupé</span>
    <span><i className="dot" style={{ background: "#F5A623" }} />Sélectionné</span>
  </div>
);

export default function BusBookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("search");
  const [tripType, setTripType] = useState("Aller simple");
  const [from, setFrom] = useState("Abidjan");
  const [to, setTo] = useState("Bouaké");
  const [pax, setPax] = useState(1);
  const [filter, setFilter] = useState("Tous");
  const [bus, setBus] = useState(null);
  const [seat, setSeat] = useState(null);
  const [opts, setOpts] = useState({ window: true, front: false, space: false, insurance: true });
  const [pass, setPass] = useState({ nom: "Cédric Dattié", tel: "+225 07 01 02 03 04", piece: "", memo: true });
  const [payMethod, setPayMethod] = useState("orange");
  const [paying, setPaying] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);
  const [bookedAt, setBookedAt] = useState("");
  const [ticketCreatedAt, setTicketCreatedAt] = useState(null);
  const [track, setTrack] = useState(null);

  useEffect(() => {
    if (step !== "details" || !bookingRef) return undefined;
    const load = () => axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/v3/tickets/track/${encodeURIComponent(bookingRef)}`, { params: { created_at: ticketCreatedAt } })
      .then((r) => { if (r.data?.ok) setTrack(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [step, bookingRef, ticketCreatedAt]);

  const dateLabel = "Sam. 28 sept. 2026";
  const total = bus
    ? bus.price * pax + (opts.window ? BUS_OPTIONS.windowSeat : 0) + (opts.front ? BUS_OPTIONS.frontSeat : 0)
      + (opts.space ? BUS_OPTIONS.space : 0) + (opts.insurance ? BUS_OPTIONS.insurance : 0)
    : 0;

  const goBack = () => {
    if (step === "details") return setStep("ticket");
    const i = STEPS.indexOf(step);
    if (step === "ticket" || i === 0) return navigate("/transport");
    setStep(STEPS[i - 1]);
  };
  const swap = () => { setFrom(to); setTo(from); };
  const filtered = BUSES.filter((b) =>
    filter === "Tous" ? true : filter === "Direct" ? b.direct : filter === "Wi-Fi" ? b.wifi : b.clim
  );
  const pay = async () => {
    if (paying) return;
    setPaying(true);
    const ref = makeRef(`AVA-${bus.company.slice(0, 3).toUpperCase()}`, from.slice(0, 3).toUpperCase(), to.slice(0, 3).toUpperCase());
    let anonId = localStorage.getItem("jsonfirst_civ_userid");
    if (!anonId) {
      anonId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `anon-${Date.now()}`;
      try { localStorage.setItem("jsonfirst_civ_userid", anonId); } catch { /* noop */ }
    }
    const token = localStorage.getItem("civ_user_token") || "";
    let fiscal = null;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/v3/transport/bookings/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          client_ref: ref, kind: "car", user_id: anonId,
          operator: { id: `op_${bus.id}`, name: bus.company },
          origin: from, destination: to, travel_date: dateLabel,
          amount_fcfa: total, pay_channel: payMethod, passenger: pass.nom,
          details: { seat: `${seat} · Fenêtre`, dep: bus.dep, arr: bus.arr },
        }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) {
        setPaying(false);
        toast.error(b.error === "amount_exceeds_jsonfirst_cap" ? "Montant trop élevé" : "Paiement impossible — réessayez");
        return;
      }
      fiscal = b.booking?.fiscal || null;
    } catch {
      setPaying(false);
      toast.error("Erreur réseau — réessayez");
      return;
    }
    setPaying(false);
    setBookingRef(ref);
    const now = new Date();
    setBookedAt(`${now.getDate()} sept. 2026 · ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
    const saved = saveTicket({
      type: "car", ref, title: `${from} → ${to}`,
      sub: `${bus.company} · ${dateLabel} · ${bus.dep}`, price: total,
      details: {
        Compagnie: bus.company, Départ: `${dateLabel} · ${bus.dep}`, Arrivée: `${dateLabel} · ${bus.arr}`, Siège: `${seat} · Fenêtre`, Voyageur: pass.nom,
        Paiement: "Payé ✓ (sandbox)",
        ...(fiscal && fiscal.number ? { "Reçu fiscal": `${fiscal.type} ${fiscal.number}` } : fiscal ? { "Reçu fiscal": "En vérification" } : {}),
      },
    });
    setTicketCreatedAt(saved.createdAt);
    setStep("confirmation");
  };
  const shareTicket = async () => {
    const text = `Billet car AYA ${bookingRef} — ${from} → ${to} ${bus.dep} (${bus.company})`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* annulé */ } }
    else { await navigator.clipboard?.writeText(text); toast.success("Billet copié ✓"); }
  };

  /* ── 8. CONFIRMATION (plein écran sombre) ── */
  if (step === "confirmation" && bus) {
    return (
      <div className="tp-page dark" data-testid="bus-confirmation">
        <div className="tp-container" style={{ paddingTop: 44 }}>
          <div style={{ textAlign: "center" }}>
            <span style={{ width: 76, height: 76, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#0E7A4F)", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 22px rgba(16,185,129,0.45)" }}><Check size={38} color="#fff" strokeWidth={3} /></span>
            <h2 style={{ margin: "14px 0 0", fontSize: 20, fontWeight: 900, color: "#fff" }}>Réservation confirmée !</h2>
            <p style={{ margin: "5px 0 0", fontSize: 12, color: "#BBE7CF", fontWeight: 600 }}>Votre billet a été acheté avec succès.</p>
          </div>
          <div className="tp-card" style={{ marginTop: 18, marginBottom: 0 }} data-testid="bus-confirm-card">
            <div style={{ display: "flex", alignItems: "center", gap: 9, paddingBottom: 9, borderBottom: "1px solid #EEF3EF" }}>
              <span className="tp-logo" style={{ background: bus.color, width: 30, height: 30 }}>{bus.company.slice(0, 3).toUpperCase()}</span>
              <span><b style={{ fontSize: 13.5 }}>{bus.company}</b><br /><span style={{ fontSize: 10.5, color: "#7B8B7F", fontWeight: 700 }}>{from} → {to}</span></span>
            </div>
            {[
              { Icon: Clock, lab: "Départ", v: `${dateLabel} · ${bus.dep}` },
              { Icon: Clock, lab: "Arrivée", v: `${dateLabel} · ${bus.arr}` },
              { Icon: Armchair, lab: "Siège", v: `${seat} · Fenêtre` },
              { Icon: User, lab: "Voyageur", v: pass.nom },
            ].map((r) => (
              <div key={r.lab} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", fontSize: 12 }}>
                <r.Icon size={14} color="#0E7A4F" style={{ flexShrink: 0 }} />
                <span style={{ color: "#7B8B7F", fontWeight: 700, width: 74 }}>{r.lab}</span>
                <b style={{ flex: 1, textAlign: "right" }}>{r.v}</b>
              </div>
            ))}
          </div>
          <div style={{ height: 14 }} />
          <PrimaryBtn testid="bus-see-ticket" onClick={() => setStep("ticket")}>Voir mon billet</PrimaryBtn>
          <div style={{ height: 9 }} />
          <button className="tp-btn ghostdark" data-testid="bus-share-confirm" onClick={shareTicket}><Share2 size={13} /> Partager le billet</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tp-page" data-testid="bus-booking-page">
      <BookingHeader title={TITLES[step]} onBack={goBack}
        right={step === "ticket" ? (
          <button className="tp-link" style={{ border: "1px solid #DCE5DD", borderRadius: 999, padding: "5px 9px", fontSize: 9.5, whiteSpace: "nowrap" }}
            data-testid="bus-wallet-btn" onClick={() => toast.success("Billet ajouté au wallet ✓")}>Ajouter au wallet</button>
        ) : null} />
      <div className="tp-container">

        {/* ── 2. RECHERCHE ── */}
        {step === "search" && (
          <>
            <Pills options={["Aller simple", "Aller-retour"]} value={tripType} onChange={setTripType} testid="bus-triptype" />
            <div style={{ fontSize: 11, fontWeight: 800, color: "#4A5D50", margin: "2px 0 6px" }}>Trajet</div>
            <div className="tp-card" style={{ position: "relative", padding: "4px 12px" }}>
              <label className="tp-input" style={{ border: "none", padding: "8px 0", marginBottom: 0, borderBottom: "1px solid #EEF3EF", borderRadius: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <MapPin size={14} color="#DC2626" />
                  <span style={{ flex: 1 }}>
                    <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bus-from-select">
                      {BUS_CITIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </span>
                </span>
              </label>
              <label className="tp-input" style={{ border: "none", padding: "8px 0", marginBottom: 0, borderRadius: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <MapPin size={14} color="#0E7A4F" />
                  <span style={{ flex: 1 }}>
                    <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="bus-to-select">
                      {BUS_CITIES.filter((c) => c !== from).map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </span>
                </span>
              </label>
              <button onClick={swap} data-testid="bus-swap-btn" aria-label="Inverser"
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", border: "1px solid #BBE7CF", background: "#EDFAF3", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ArrowUpDown size={14} color="#0E7A4F" />
              </button>
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#4A5D50", margin: "2px 0 6px" }}>Date de départ</div>
            <div className="tp-input">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Calendar size={15} color="#0E7A4F" />
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{dateLabel}</span>
              </span>
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#4A5D50", margin: "2px 0 6px" }}>Nombre de voyageurs</div>
            <div className="tp-input">
              <span style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <Stepper value={pax} onChange={setPax} unit="" />
              </span>
            </div>
            <PrimaryBtn testid="bus-search-btn" onClick={() => setStep("results")}>Rechercher des trajets <ArrowRight size={15} /></PrimaryBtn>
            <div className="tp-card" style={{ marginTop: 12, padding: 0, overflow: "hidden", position: "relative", background: "#0B3B27", border: "none", color: "#fff", minHeight: 86 }}>
              <img src="/aya/transport/tp_protection.jpg" alt="" style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "42%", objectFit: "cover" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, #0B3B27 55%, rgba(11,59,39,0.35))" }} />
              <div style={{ position: "relative", display: "flex", gap: 10, alignItems: "center", padding: 12 }}>
                <span className="tp-logo" style={{ background: "#10B981", width: 32, height: 32, borderRadius: "50%" }}><Check size={15} /></span>
                <span style={{ fontSize: 11, lineHeight: 1.4, maxWidth: "62%" }}><b>Voyagez en toute sérénité</b><br />
                  <span style={{ color: "#BBE7CF" }}>Choisissez votre siège, ajoutez une assurance et payez en toute sécurité.</span></span>
              </div>
            </div>
          </>
        )}

        {/* ── 3. RÉSULTATS ── */}
        {step === "results" && (
          <>
            <div style={{ textAlign: "center", margin: "10px 0 8px" }}>
              <b style={{ fontSize: 14.5 }}>{from} → {to}</b>
              <div style={{ fontSize: 10.5, color: "#7B8B7F", fontWeight: 700 }}>{dateLabel} · {pax} voyageur{pax > 1 ? "s" : ""} · <button className="tp-link" style={{ padding: 0 }} data-testid="bus-modify-btn" onClick={() => setStep("search")}>Modifier</button></div>
            </div>
            <div className="tp-chips" data-testid="bus-filters">
              {["Tous", "Direct", "Wi-Fi", "Climatisé"].map((f) => (
                <button key={f} className={`tp-chip ${filter === f ? "on" : ""}`} data-testid={`bus-filter-${f}`} onClick={() => setFilter(f)}>{f}</button>
              ))}
            </div>
            {filtered.map((b) => (
              <div key={b.id} className="tp-result" data-testid={`bus-result-${b.id}`} style={{ cursor: "default" }}>
                <span className="toprow" style={{ alignItems: "flex-start" }}>
                  <span>
                    <span className="tp-airline" style={{ color: b.color, fontSize: 13 }}>
                      <span className="tp-logo" style={{ background: b.color, width: 30, height: 30, borderRadius: 9 }}>{b.company.slice(0, 3).toUpperCase()}</span>
                      {b.company}
                    </span>
                    <div style={{ marginTop: 3 }}><RatingLine rating={b.rating} reviews={b.reviews} /></div>
                  </span>
                  <span className="tp-price" style={{ color: "#0E7A4F" }}>{fmt(b.price)}</span>
                </span>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: 6 }}>
                  <span>
                    <span style={{ fontSize: 17, fontWeight: 900 }}>{b.dep} <span style={{ color: "#7B8B7F" }}>→</span> {b.arr}</span>
                    <div style={{ fontSize: 9.5, color: "#7B8B7F", fontWeight: 700, marginTop: 3, display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                      {b.duration}min {b.direct && <span className="tp-tag">Direct</span>}
                      {b.clim && <span><Snowflake size={10} style={{ verticalAlign: -1 }} /> Climatisé</span>}
                      {b.wifi && <span><Wifi size={10} style={{ verticalAlign: -1 }} /> Wi-Fi</span>}
                    </div>
                  </span>
                  <button className="tp-btn" style={{ width: "auto", height: 31, padding: "0 17px", fontSize: 11 }} data-testid={`bus-choose-${b.id}`}
                    onClick={() => { setBus(b); setSeat(null); setStep("seat"); }}>Choisir</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── 4. CHOIX DU SIÈGE ── */}
        {step === "seat" && bus && (
          <>
            <BusSummary bus={bus} from={from} to={to} dateLabel={dateLabel} />
            <BusLegend />
            <BusSeatMap taken={BUS_TAKEN} selected={seat} onSelect={setSeat} />
            <div className="tp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="bus-seat-summary">
              <span><span style={{ fontSize: 10, color: "#7B8B7F", fontWeight: 700 }}>Siège sélectionné</span><br />
                <b style={{ fontSize: 15 }}>{seat ? `${seat} · Fenêtre` : "—"}</b></span>
              <b style={{ color: "#0E7A4F" }}>{seat ? `+ ${fmt(BUS_OPTIONS.windowSeat)}` : ""}</b>
            </div>
            <PrimaryBtn testid="bus-seat-continue" disabled={!seat} onClick={() => setStep("options")}>Continuer <ArrowRight size={15} /></PrimaryBtn>
          </>
        )}

        {/* ── 5. OPTIONS ET ASSURANCE ── */}
        {step === "options" && bus && (
          <>
            <BusSummary bus={bus} from={from} to={to} dateLabel={dateLabel} />
            <div className="tp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span><span style={{ fontSize: 10, color: "#7B8B7F", fontWeight: 700 }}>Votre siège</span><br />
                <b><Armchair size={13} style={{ verticalAlign: -2 }} color="#0E7A4F" /> {seat} · Fenêtre</b></span>
              <span style={{ textAlign: "right" }}>
                <button className="tp-link" style={{ display: "block", marginLeft: "auto" }} data-testid="bus-seat-modify" onClick={() => setStep("seat")}>Modifier</button>
                <b style={{ color: "#0E7A4F", fontSize: 12 }}>+ {fmt(BUS_OPTIONS.windowSeat)}</b>
              </span>
            </div>
            <div className="tp-sechead"><div><h2>Options</h2></div></div>
            <OptRow testid="bus-opt-window" title="Siège côté fenêtre" sub="Vue dégagée pendant le trajet" price={`+ ${fmt(BUS_OPTIONS.windowSeat)}`}
              checked={opts.window} onToggle={() => setOpts((o) => ({ ...o, window: !o.window }))} />
            <OptRow testid="bus-opt-front" title="Siège avant" sub="Accès plus rapide" price={`+ ${fmt(BUS_OPTIONS.frontSeat)}`}
              checked={opts.front} onToggle={() => setOpts((o) => ({ ...o, front: !o.front }))} />
            <OptRow testid="bus-opt-space" title="Plus d'espace" sub="Plus de confort" price={`+ ${fmt(BUS_OPTIONS.space)}`}
              checked={opts.space} onToggle={() => setOpts((o) => ({ ...o, space: !o.space }))} />
            <div className="tp-sechead"><div><h2>Protection voyage</h2></div></div>
            <div className="tp-optrow" data-testid="bus-opt-insurance" onClick={() => setOpts((o) => ({ ...o, insurance: !o.insurance }))} style={{ alignItems: "flex-start" }}>
              <span className={`tp-check ${opts.insurance ? "on" : ""}`} style={{ marginTop: 2 }}>{opts.insurance && <Check size={13} color="#fff" strokeWidth={3} />}</span>
              <span className="t">
                <h4>Assurance voyage</h4>
                <ul style={{ margin: "5px 0 4px", paddingLeft: 14, fontSize: 10, color: "#7B8B7F", lineHeight: 1.6 }}>
                  <li>Couverture en cas d'accident</li>
                  <li>Assistance pendant le trajet</li>
                  <li>Indemnisation selon garanties</li>
                </ul>
                <button className="tp-link" style={{ padding: 0 }} data-testid="bus-insurance-details"
                  onClick={(e) => { e.stopPropagation(); toast("Garanties complètes disponibles dans les conditions AYA Protection."); }}>Voir les détails</button>
              </span>
              <span className="px">+ {fmt(BUS_OPTIONS.insurance)}</span>
            </div>
            <div className="tp-card"><PriceRow total label="Total actuel" value={fmt(total)} /></div>
            <PrimaryBtn testid="bus-options-continue" onClick={() => setStep("passenger")}>Continuer <ArrowRight size={15} /></PrimaryBtn>
          </>
        )}

        {/* ── 6. INFORMATIONS VOYAGEURS ── */}
        {step === "passenger" && (
          <>
            <div className="tp-sechead"><div><h2>Voyageur 1</h2></div></div>
            <label className="tp-input"><label>Nom complet</label>
              <input value={pass.nom} onChange={(e) => setPass((p) => ({ ...p, nom: e.target.value }))} data-testid="bus-pass-nom" placeholder="Nom complet" />
            </label>
            <label className="tp-input"><label>Téléphone</label>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input value={pass.tel} onChange={(e) => setPass((p) => ({ ...p, tel: e.target.value }))} data-testid="bus-pass-tel" placeholder="+225 ..." />
                <span style={{ width: 26, height: 26, borderRadius: 8, background: "#EDFAF3", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Contact size={14} color="#0E7A4F" /></span>
              </span>
            </label>
            <label className="tp-input"><label>Pièce d'identité (optionnel)</label>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input value={pass.piece} onChange={(e) => setPass((p) => ({ ...p, piece: e.target.value }))} data-testid="bus-pass-piece" placeholder="Carte nationale / Passeport" />
                <span style={{ width: 26, height: 26, borderRadius: 8, background: "#F2F6F3", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Camera size={14} color="#4A5D50" /></span>
              </span>
            </label>
            <OptRow testid="bus-pass-memo" title="Mémoriser mes informations" sub="Pour mes prochains voyages" price=""
              checked={pass.memo} onToggle={() => setPass((p) => ({ ...p, memo: !p.memo }))} />
            <PrimaryBtn testid="bus-pass-continue" disabled={!pass.nom.trim() || !pass.tel.trim()} onClick={() => setStep("payment")}>Continuer <ArrowRight size={15} /></PrimaryBtn>
          </>
        )}

        {/* ── 7. PAIEMENT ── */}
        {step === "payment" && bus && (
          <>
            <BusSummary bus={bus} from={from} to={to} dateLabel={dateLabel} />
            <div className="tp-card" data-testid="bus-pay-recap">
              <PriceRow label={`Siège ${seat} · Fenêtre${pax > 1 ? ` × ${pax}` : ""}`} value={fmt(bus.price * pax)} />
              {opts.window && <PriceRow label="Option fenêtre" value={`+ ${fmt(BUS_OPTIONS.windowSeat)}`} />}
              {opts.front && <PriceRow label="Siège avant" value={`+ ${fmt(BUS_OPTIONS.frontSeat)}`} />}
              {opts.space && <PriceRow label="Plus d'espace" value={`+ ${fmt(BUS_OPTIONS.space)}`} />}
              {opts.insurance && <PriceRow label="Assurance voyage" value={`+ ${fmt(BUS_OPTIONS.insurance)}`} />}
              <PriceRow total label="Total" value={fmt(total)} />
            </div>
            <div className="tp-sechead"><div><h2>Choisir un moyen de paiement</h2></div></div>
            <div className="tp-paytiles" data-testid="tp-pay-methods">
              {MOBILE_PAY.map((m) => (
                <button key={m.id} className={`tp-paytile ${payMethod === m.id ? "on" : ""}`} data-testid={`tp-pay-${m.id}`} onClick={() => setPayMethod(m.id)}>
                  <span className="tp-paylogo" style={{ background: m.bg, color: m.dark ? "#1F2937" : "#fff" }}>{m.text}</span>
                  {m.name}
                </button>
              ))}
            </div>
            <div className={`tp-payrow ${payMethod === "card" ? "on" : ""}`} data-testid="tp-pay-card" onClick={() => setPayMethod("card")}>
              <span className="tp-paylogo" style={{ background: "#334155" }}><CreditCard size={13} /></span>
              <h4>Carte bancaire</h4>
              <span className={`tp-radio ${payMethod === "card" ? "on" : ""}`} />
            </div>
            <PrimaryBtn testid="bus-pay-btn" disabled={paying} onClick={pay}>
              {paying ? "Paiement en cours…" : <>Payer {fmt(total)} <ArrowRight size={15} /></>}
            </PrimaryBtn>
            <SecureNote />
          </>
        )}

        {/* ── 9. BILLET ÉLECTRONIQUE ── */}
        {step === "ticket" && bus && (
          <>
            <div className="tp-btick" data-testid="tp-ticket">
              <div className="toprow">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 900, fontSize: 14 }}>
                  <span className="tp-logo" style={{ background: bus.color, width: 30, height: 30, borderRadius: 9 }}>{bus.company.slice(0, 3).toUpperCase()}</span>
                  {bus.company}
                </span>
                <span className="tp-tag" style={{ background: "#0E7A4F", borderColor: "#0E7A4F", color: "#fff", padding: "4px 11px", borderRadius: 999 }}>Billet validé</span>
              </div>
              <div className="qr">
                <QRCodeSVG value={bookingRef} size={158} level="M" data-testid="tp-ticket-qr" />
                <div style={{ fontSize: 11.5, fontWeight: 800, marginTop: 9 }}>{bookingRef}</div>
                <div style={{ fontSize: 14, fontWeight: 900, marginTop: 2 }}>{from} <span style={{ color: "#7B8B7F" }}>→</span> {to}</div>
              </div>
              <div className="grid2">
                <span><span className="lab"><Clock size={9} style={{ verticalAlign: -1 }} /> Départ</span><span className="v">28 sept. 2026<br />{bus.dep}</span></span>
                <span><span className="lab"><Clock size={9} style={{ verticalAlign: -1 }} /> Arrivée</span><span className="v">28 sept. 2026<br />{bus.arr}</span></span>
                <span><span className="lab"><Armchair size={9} style={{ verticalAlign: -1 }} /> Siège</span><span className="v">{seat} · Fenêtre</span></span>
                <span><span className="lab"><User size={9} style={{ verticalAlign: -1 }} /> Voyageur</span><span className="v">{pass.nom}</span></span>
              </div>
            </div>
            <div className="tp-tbtns">
              <PrimaryBtn ghost testid="bus-ticket-share" onClick={shareTicket}><Share2 size={13} /> Partager</PrimaryBtn>
              <PrimaryBtn ghost testid="bus-ticket-download" onClick={() => toast.success("Billet enregistré dans votre espace AYA ✓")}><Download size={13} /> Télécharger</PrimaryBtn>
              <button className="tp-btn ghost" style={{ height: 40, fontSize: 12.5, borderColor: "#FCA5A5", color: "#DC2626" }} data-testid="bus-ticket-cancel"
                onClick={() => toast("Annulation possible jusqu'à 24h avant le départ. Contactez l'assistance.")}><XCircle size={13} /> Annuler</button>
            </div>
            <div style={{ height: 9 }} />
            <PrimaryBtn testid="bus-trip-details" onClick={() => setStep("details")}>Détails du voyage <ArrowRight size={15} /></PrimaryBtn>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="bus-all-tickets" onClick={() => navigate("/mes-billets")}>Tous mes billets</PrimaryBtn>
          </>
        )}

        {/* ── 10. DÉTAILS ET SUIVI DU VOYAGE ── */}
        {step === "details" && bus && (() => {
          const st = track?.status || "confirmed";
          const BADGES = {
            confirmed: { txt: "À venir", bg: "#EDFAF3", col: "#0E7A4F", bd: "#BBE7CF" },
            boarding: { txt: "Embarquement", bg: "#FEF6E7", col: "#B45309", bd: "#FCD9A0" },
            enroute: { txt: `En route · ${track?.progress ?? 0}%`, bg: "#EAF2FE", col: "#1D4ED8", bd: "#BFDBFE" },
            arrived: { txt: "Arrivé ✓", bg: "#0E7A4F", col: "#fff", bd: "#0E7A4F" },
          };
          const b = BADGES[st];
          const departed = st === "enroute" || st === "arrived";
          return (
            <>
              <BusSummary bus={bus} from={from} to={to} dateLabel={dateLabel}
                right={<span className="tp-tag" data-testid="bus-track-badge" style={{ background: b.bg, color: b.col, borderColor: b.bd, padding: "4px 10px", borderRadius: 999 }}>{b.txt}</span>} />
              <div className="tp-card" data-testid="bus-live-tracking">
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: st === "arrived" ? "#0E7A4F" : "#DC2626", boxShadow: st === "arrived" ? "none" : "0 0 0 4px rgba(220,38,38,0.15)" }} />
                  <b style={{ fontSize: 12.5, flex: 1 }}>Suivi en direct</b>
                  <span style={{ fontSize: 9, color: "#7B8B7F", fontWeight: 700 }}>{track ? "Actualisé à l'instant" : "Connexion…"}</span>
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#10241A" }} data-testid="bus-track-label">{track?.label || "Billet confirmé · embarquement bientôt"}</div>
                <div style={{ height: 8, borderRadius: 999, background: "#E8ECE9", marginTop: 8, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${track?.progress ?? 0}%`, background: "linear-gradient(90deg,#10B981,#0E7A4F)", borderRadius: 999, transition: "width 0.8s" }} data-testid="bus-track-progress" />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: 10, color: "#7B8B7F", fontWeight: 700 }}>
                  <span><MapPin size={10} style={{ verticalAlign: -1 }} color="#0E7A4F" /> {track?.position || "Gare routière d'Adjamé"}</span>
                  <span>{track?.km_left ?? 348} km restants · arrivée {bus.arr}</span>
                </div>
              </div>
              <div className="tp-card" data-testid="bus-details-timeline">
                <div className="tp-timeline">
                  <div className="tp-tstep"><span className="nd" /><h4>Billet confirmé</h4><p>{bookedAt}</p></div>
                  <div className={`tp-tstep ${departed ? "" : "hollow"}`}><span className="nd" /><h4>Départ{departed ? " ✓" : ""}</h4><p>28 sept. 2026 · {bus.dep}<br />Gare routière d'Adjamé</p></div>
                  <div className={`tp-tstep ${st === "arrived" ? "" : "hollow"}`}><span className="nd" /><h4>Arrivée{st === "arrived" ? " ✓" : ""}</h4><p>28 sept. 2026 · {bus.arr}<br />Gare routière de {to}</p></div>
                </div>
              </div>
            <div className="tp-tbtns">
              <PrimaryBtn ghost testid="bus-details-modify" onClick={() => toast("Modification / annulation possible jusqu'à 24h avant le départ.")}><RefreshCw size={13} /> Modifier / Annuler</PrimaryBtn>
              <PrimaryBtn ghost testid="bus-details-assist" onClick={() => navigate("/aya-prive?ctx=" + encodeURIComponent(`Assistance billet de car AYA ${bookingRef} (${from} → ${to}).`))}><LifeBuoy size={13} /> Assistance</PrimaryBtn>
            </div>
              <div className="tp-card" style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <img src="/aya/transport/tp_car.jpg" alt="Gare routière d'Adjamé" style={{ width: 62, height: 46, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  <b style={{ fontSize: 12.5 }}><MapPin size={12} color="#0E7A4F" style={{ verticalAlign: -1 }} /> Gare routière d'Adjamé</b><br />
                  <button className="tp-link" style={{ padding: 0 }} data-testid="bus-station-map" onClick={() => navigate("/map-explorer")}>Voir sur la carte</button>
                </span>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
